import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { Link } from "@tanstack/react-router";
import { AlertCircle, CheckCircle, Clock, Loader2 } from "lucide-react";

export type OAuthCallbackStatus = "processing" | "success" | "error";

interface OAuthCallbackCardProps {
  status: OAuthCallbackStatus;
  errorMessage?: string | null;
  isStateExpired?: boolean;
  backLabel?: string;
  backTo?: string;
  backParams?: Record<string, string>;
  backSearch?: Record<string, string>;
  onBack?: () => void;
}

export function OAuthCallbackCard({
  status,
  errorMessage,
  isStateExpired = false,
  backLabel = "Go to home",
  backTo,
  backParams,
  backSearch,
  onBack,
}: OAuthCallbackCardProps) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            {status === "processing" && (
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            )}
            {status === "success" && (
              <CheckCircle className="h-12 w-12 text-green-500" />
            )}
            {status === "error" && isStateExpired && (
              <Clock className="h-12 w-12 text-amber-500" />
            )}
            {status === "error" && !isStateExpired && (
              <AlertCircle className="h-12 w-12 text-destructive" />
            )}
          </div>
          <CardTitle>
            {status === "processing" && "Connecting Account..."}
            {status === "success" && "Account Connected!"}
            {status === "error" &&
              (isStateExpired ? "Session Expired" : "Connection Failed")}
          </CardTitle>
          <CardDescription>
            {status === "processing" &&
              "Please wait while we complete the authorization."}
            {status === "success" &&
              "Your social media account has been connected. Redirecting..."}
            {status === "error" &&
              (errorMessage || "An unexpected error occurred.")}
          </CardDescription>
        </CardHeader>
        {status === "error" && (backTo || onBack) && (
          <CardContent className="text-center">
            {backTo ? (
              <Link
                to={backTo}
                params={backParams}
                search={backSearch}
                className="text-primary hover:underline"
              >
                {backLabel}
              </Link>
            ) : (
              <button
                type="button"
                onClick={onBack}
                className="text-primary hover:underline"
              >
                {backLabel}
              </button>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
